import React, { useEffect, useState } from "react";
import { Dashboard, ReactLoading } from "../Dashboard/TinManagement/import";
import {
  Button,
  Card,
  Drawer,
  Input,
  message,
  Popconfirm,
  Select,
  Switch,
  Table,
  Result
} from "antd";
import {
  DeleteOutlined,
  EditFilled,
  PlusOutlined,
  SaveOutlined,
  SearchOutlined,
  StopOutlined
} from "@ant-design/icons";
import useAxios from "../../hooks/useAxios";
import { getAllUsers } from "../../services/users/users.service";
import { checkBulkPermissions } from "../../utils/checkPermission";
import useRole from "../../hooks/useRole";

const { Option } = Select;

const inputStyles = {
  padding: "10px 11px",
  fontSize: "16px",
  lineHeight: 1.5,
  borderRadius: "8px",
};

const AmeyoConf = () => {
  const [openUserModal, setOpenUserModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ameyoData, setAmeyoData] = useState(null);
  const [recruiters, setRecruiters] = useState([]);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const api = useAxios();

  const role = useRole();
  const [permissions, setPermissions] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
  });
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!role) return;
      try {
        const checks = [
          { entityType: "ameyo_configuration_module", permission: "view_ameyo_configuration" },
          { entityType: "ameyo_configuration_module", permission: "add_ameyo_campaign" },
          { entityType: "ameyo_configuration_module", permission: "edit_ameyo_campaign" },
          { entityType: "ameyo_configuration_module", permission: "delete_ameyo_campaign" }
        ];
        const results = await checkBulkPermissions(role, checks);

        const perms = {
          canView: false,
          canAdd: false,
          canEdit: false,
          canDelete: false,
        };
        results.forEach(res => {
          if (res.permission === "view_ameyo_configuration") perms.canView = res.allowed;
          if (res.permission === "add_ameyo_campaign") perms.canAdd = res.allowed;
          if (res.permission === "edit_ameyo_campaign") perms.canEdit = res.allowed;
          if (res.permission === "delete_ameyo_campaign") perms.canDelete = res.allowed;
        });

        setPermissions(perms);
      } catch (err) {
        console.error(err);
      } finally {
        setCheckingPermissions(false);
      }
    };

    fetchPermissions();
  }, [role]);

  const [rows, setRows] = useState([
    {
      key: 1,
      recruiter: "Abhishek Poddar",
      campaignId: "23432",
      leadIdOne: "2",
      leadIdTwo: "10",
      status: true,
    },
    {
      key: 2,
      recruiter: "John Doe",
      campaignId: "23987",
      leadIdOne: "5",
      leadIdTwo: "7",
      status: false,
    },
  ]);

  const toggleUserModal = () => setOpenUserModal(!openUserModal);

  const columns = [
    {
      title: "RECRUITER",
      dataIndex: "recruiter",
      key: "recruiter",
    },
    {
      title: "CAMPAIGN ID",
      dataIndex: "campaignId",
      key: "campaignId",
    },
    {
      title: "LEAD ID ONE",
      dataIndex: "leadIdOne",
      key: "leadIdOne",
    },
    {
      title: "LEAD ID TWO",
      dataIndex: "leadIdTwo",
      key: "leadIdTwo",
    },
    {
      title: "ACTIONS",
      dataIndex: "action",
      key: "action",
      fixed: "right",
      render: (value, row) => {
        const { campaignId, leadIdOne, leadIdTwo, recruiterId } = row || {};

        return (
          <div className="flex gap-3">
            {permissions.canEdit && (
              <span
                onClick={() => {
                  setAmeyoData({
                    _id: value,
                    recruiter: recruiterId,
                    campaignId,
                    leadIdOne,
                    leadIdTwo,
                  });
                  setOpenUserModal(true);
                }}
                className="cursor-pointer"
              >
                <EditFilled style={{ fontSize: "1.2rem", color: "grey" }} />
              </span>
            )}
            {permissions.canDelete && (
              <span className="cursor-pointer">
                <Popconfirm
                  title="Are you sure you want to delete the configuration?"
                  description="Are you sure you want to delete the configuration?"
                  onConfirm={() => deleteConfiguration(value)}
                  okText="Yes"
                  cancelText="No"
                  placement="left"
                  okButtonProps={{
                    className: "!bg-red-500 hover:!bg-red-600 !border-none",
                  }}
                >
                  <DeleteOutlined className="!text-red-500 !text-[1.2rem]" />
                </Popconfirm>
              </span>
            )}
          </div>
        );
      },
    },
  ];

  const checkValidation = () => {
    let e = {};
    const recruiter = ameyoData?.recruiter?.trim();
    const campaignId = ameyoData?.campaignId?.trim();
    const leadIdOne = ameyoData?.leadIdOne?.trim();
    const leadIdTwo = ameyoData?.leadIdTwo?.trim();

    if (!recruiter) {
      e.recruiter = "Recruiter is required.";
    }

    if (!campaignId) {
      e.campaignId = "Campaign is required.";
    }

    if (!leadIdOne) {
      e.leadIdOne = "Lead Id 1 is required.";
    }

    if (!leadIdTwo) {
      e.leadIdTwo = "Lead Id 2 is required.";
    }

    setError(e);

    return Object.keys(e).length === 0;
  };

  const saveConfiguration = async () => {
    const isProceed = checkValidation();

    if (!isProceed) return;

    try {
      setLoading(true);
      const recruiter = ameyoData?.recruiter?.trim();
      const campaignId = ameyoData?.campaignId?.trim();
      const leadIdOne = ameyoData?.leadIdOne?.trim();
      const leadIdTwo = ameyoData?.leadIdTwo?.trim();
      await api.post(
        `/recruiter-compaign-setup`,
        {
          recruiterId: recruiter,
          compaignId: campaignId,
          priorityLeadId: leadIdOne,
          nonPriorityLeadId: leadIdTwo,
        },
        { tokenType: "user" },
      );
      await getAmeyoData();
      handleClose();
      message.success(`Configuration Added Successfully.`);
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const updateConfiguration = async () => {
    const isProceed = checkValidation();

    if (!isProceed) return;

    try {
      setLoading(true);
      const recruiter = ameyoData?.recruiter?.trim();
      const campaignId = ameyoData?.campaignId?.trim();
      const leadIdOne = ameyoData?.leadIdOne?.trim();
      const leadIdTwo = ameyoData?.leadIdTwo?.trim();
      await api.put(
        `/recruiter-compaign-setup/${ameyoData?._id}`,
        {
          recruiterId: recruiter,
          compaignId: campaignId,
          priorityLeadId: leadIdOne,
          nonPriorityLeadId: leadIdTwo,
          status: true,
          isActive: true,
        },
        { tokenType: "user" },
      );
      await getAmeyoData();
      handleClose();
      message.success(`Configuration Updated Successfully.`);
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const getAmeyoData = async () => {
    try {
      const { data } = await api.get(`/recruiter-compaign-setup`, {
        tokenType: "user",
      });
      const row = [];

      for (let item of data) {
        row.push({
          key: item?._id,
          action: item?._id,
          campaignId: String(item?.compaignId),
          recruiter: item?.recruiterId?.name,
          recruiterId: item?.recruiterId?._id,
          leadIdOne: String(item?.priorityLeadId),
          leadIdTwo: String(item?.nonPriorityLeadId),
        });
      }
      setRows(row);
    } catch (err) {
      console.error(err);
    }
  };

  const getRecruiters = async () => {
    try {
      const { data } = await getAllUsers(api, "user");
      const _rows = data?.filter((ele) => ele?.role === "interviewer");
      setRecruiters(_rows);
    } catch (err) {
      console.error(err);
    }
  };

  const deleteConfiguration = async (id) => {
    try {
      setLoading(true);
      await api.delete(`/recruiter-compaign-setup/${id}`, {
        tokenType: "user",
      });
      await getAmeyoData();
      message.success("Configuration Deleted Successfully.");
    } catch (err) {
      console.error(err);
      const error = err?.response?.data?.error;
      message.error(error ? error : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        await Promise.all([getAmeyoData(), getRecruiters()]);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const filteredRows = rows.filter((row) => {
    const _search = search.toLowerCase();

    return (
      row?.recruiter?.toLowerCase().includes(_search) ||
      row?.campaignId?.toLowerCase().includes(_search) ||
      row?.leadIdOne?.toLowerCase().includes(_search) ||
      row?.leadIdTwo?.toLowerCase().includes(_search)
    );
  });

  const handleInputChange = (e) => {
    setAmeyoData({
      ...ameyoData,
      [e.target.name]: e.target.value,
    });
  };

  const handleClose = () => {
    toggleUserModal();
    setAmeyoData(null);
  };

  return (
    <Dashboard title="User Management">
      {checkingPermissions ? (
        <div className="flex justify-center items-center h-64">
          <ReactLoading type="spin" color="#0000FF" />
        </div>
      ) : !permissions.canView ? (
        <Card className="mt-3">
          <Result
            icon={<StopOutlined style={{ color: '#ff4d4f' }} />}
            title="You don't have permission to access this page"
            subTitle="Please contact your administrator if you believe this is an error."
          />
        </Card>
      ) : (
        <>
          {loading && (
            <ReactLoading type="spin" color="#0000FF" className="spinnerBg" />
          )}
          <div className="py-1 px-1 mb-4">
            <h4 className="text-[1.5rem] mb-0">Ameyo Configuration</h4>
          </div>
          <div>
            <Card>
              <div className="flex flex-col gap-[1.5rem]">
                <div className="flex justify-between items-center">
                  <div className="flex-grow">
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{
                        borderRadius: "8px",
                        width: "100%",
                        maxWidth: "300px",
                      }}
                      size="large"
                      placeholder="Search by recruiter and campaign..."
                      prefix={<SearchOutlined />}
                    />
                  </div>
                  <div className="flex gap-1">
                    {permissions.canAdd && (
                      <button
                        onClick={toggleUserModal}
                        data-testid="add-new"
                        className="btn btn-primary flex gap-[10px] items-center"
                      >
                        <PlusOutlined
                          style={{ fontSize: "1.1rem", fontWeight: 600 }}
                        />
                        <span className="flex items-center text-[16px] font-semibold">
                          Add Campaign
                        </span>
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <Table
                    columns={columns}
                    dataSource={filteredRows}
                    pagination={{
                      total: filteredRows?.length,
                      pageSizeOptions: ["5", "10", "30"],
                      showSizeChanger: true,
                      //   current: 1,
                      //   onChange: () => {},
                    }}
                    showSorterTooltip={false}
                    scroll={{ x: true }}
                    style={{ whiteSpace: "pre" }}
                    exportFileName="details"
                    size="small"
                  />
                </div>
              </div>
            </Card>
          </div>
          <Drawer
            title="New Configuration"
            width={550}
            placement="right"
            zIndex={9}
            closeIcon={false}
            onClose={handleClose}
            open={openUserModal}
            extra={<Button onClick={handleClose}>X</Button>}
          >
            <div className="d-flex flex-column gap-3" style={{ height: "100%" }}>
              <div className="row" style={{ flex: 1 }}>
                <div className="col-12">
                  <div className="row">
                    <div className="col-12">
                      <div className="w-100 d-flex flex-column gap-1">
                        <div className="row">
                          <div className="col-12 mb-4 mt-0">
                            <div className="mb-[24px]">
                              <Select
                                size="large"
                                showSearch
                                getPopupContainer={(triggerNode) =>
                                  triggerNode.parentElement
                                }
                                className="w-100 p-0 outline-none bg-white"
                                placeholder="Select Recruiter"
                                value={ameyoData?.recruiter || null}
                                optionFilterProp="children"
                                filterOption={(input, option) => {
                                  return (
                                    option?.children
                                      ?.toLowerCase()
                                      .indexOf(input.toLowerCase()) >= 0 ||
                                    option.value
                                      .toLowerCase()
                                      .indexOf(input.toLowerCase()) >= 0
                                  );
                                }}
                                onChange={(value) => {
                                  setAmeyoData({
                                    ...ameyoData,
                                    recruiter: value,
                                  });
                                }}
                                status={
                                  error?.recruiter && error?.recruiter
                                    ? "error"
                                    : ""
                                }
                              >
                                {recruiters
                                  ?.sort((a, b) =>
                                    a?.name
                                      ?.trim()
                                      .toLowerCase()
                                      .localeCompare(b?.name?.trim().toLowerCase()),
                                  )
                                  ?.map((user, index) => {
                                    if (
                                      user?.role === "interviewer" &&
                                      user.IsUserActive
                                    ) {
                                      return (
                                        <Option key={index} value={user?._id}>
                                          {user?.name}
                                        </Option>
                                      );
                                    }
                                  })}
                              </Select>
                              {error?.recruiter && (
                                <div className="text-[#ff4d4f]">
                                  {error?.recruiter}
                                </div>
                              )}
                            </div>
                            <div className="mb-[24px]">
                              <Input
                                type="text"
                                name="campaignId"
                                onChange={handleInputChange}
                                value={ameyoData?.campaignId ?? null}
                                style={inputStyles}
                                allowClear
                                size="large"
                                placeholder="Campaign Id"
                                status={
                                  error?.campaignId && error?.campaignId
                                    ? "error"
                                    : ""
                                }
                              />
                              {error?.campaignId && (
                                <div className="text-[#ff4d4f]">
                                  {error?.campaignId}
                                </div>
                              )}
                            </div>
                            <div className="mb-[24px]">
                              <Input
                                type="text"
                                name="leadIdOne"
                                onChange={handleInputChange}
                                value={ameyoData?.leadIdOne ?? null}
                                style={inputStyles}
                                allowClear
                                size="large"
                                placeholder="Lead Id 1"
                                status={
                                  error?.leadIdOne && error?.leadIdOne
                                    ? "error"
                                    : ""
                                }
                              />
                              {error?.leadIdOne && (
                                <div className="text-[#ff4d4f]">
                                  {error?.leadIdOne}
                                </div>
                              )}
                            </div>
                            <div className="mb-[24px]">
                              <Input
                                type="text"
                                name="leadIdTwo"
                                onChange={handleInputChange}
                                value={ameyoData?.leadIdTwo ?? null}
                                style={inputStyles}
                                allowClear
                                size="large"
                                placeholder="Lead Id 2"
                                status={
                                  error?.leadIdTwo && error?.leadIdTwo
                                    ? "error"
                                    : ""
                                }
                              />
                              {error?.leadIdTwo && (
                                <div className="text-[#ff4d4f]">
                                  {error?.leadIdTwo}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row pb-4">
                <div className="col-12">
                  <div className="pt-3 d-flex gap-1">
                    <button
                      onClick={handleClose}
                      className="save-filter-btn text-black rounded px-4 py-2 flex-1 text-[16px] font-semibold"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        ameyoData?._id
                          ? updateConfiguration()
                          : saveConfiguration();
                      }}
                      className="btn btn-primary flex gap-[10px] items-center justify-center flex-1"
                    >
                      <SaveOutlined
                        style={{ fontSize: "1.1rem", fontWeight: 600 }}
                      />
                      <span className="flex items-center text-[16px] font-semibold text-center">
                        {ameyoData?._id ? "Update" : "Save"}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Drawer>
        </>
      )}
    </Dashboard>
  );
};

export default AmeyoConf;
